/**
 * --------------------------------------------------------------------------
 * Dialog Popover Web Component
 * --------------------------------------------------------------------------
 * This component handles dialog popover functionality with body scroll
 * prevention when the dialog is open and restoration when closed.
 */
if (!customElements.get('dialog-popover')) {
    class DialogPopover extends HTMLElement {
        static CONSTANTS = {
            SCROLL_LOCK_CLASS: 'dialog-scroll-locked',
            SCROLL_POSITION_ATTRIBUTE: 'data-scroll-position',
        };
        static globalClickHandlerAdded = false;
        static openingModalId = null; // Track which modal is currently opening

        constructor() {
            super();
            this.dialog = null;
            this.backdrop = null;
            this.isScrollLocked = false;
            this.originalScrollPosition = 0;
            this.shouldScrollToTop = false;
            this.shouldPreventBodyScroll = true;
            this.shouldPreventClose = false;
            this.isProgrammaticClose = false;
            this.wrappedHidePopover = null;
            this.isOpeningAnotherModal = false;
            this.isHoverMode = false;
            this.hoverCloseTimeout = null;
            this.triggerButtons = [];
        }

        connectedCallback() {
            if (this.initialize()) {
                this.addEventListeners();
            }
        }

        disconnectedCallback() {
            this.removeEventListeners();
            this.restoreBodyScroll();
        }

        // --------------------------------------------------------------------
        // SETUP & INITIALIZATION
        // --------------------------------------------------------------------

        /**
         * Initializes the component and validates the dialog element.
         * @returns {boolean} True if initialization is successful, false otherwise.
         */
        initialize() {
            // Look for either a native popover element or a popover-element custom element
            this.dialog = this.querySelector('[popover]');
            
            // If no native popover found, check for popover-element custom element (fallback mode)
            if (!this.dialog) {
                this.dialog = this.querySelector('popover-element');
            }
            
            if (!this.dialog) {
                console.error('DialogPopover Error: Could not find an element with popover attribute or popover-element custom element inside the component.', this);
                return false;
            }
            
            // Read the scroll-to-top attribute
            const scrollToTopAttr = this.getAttribute('scroll-to-top');
            this.shouldScrollToTop = scrollToTopAttr === 'true' || scrollToTopAttr === '1';

            // Read the prevent-body-scroll attribute (default true if not provided)
            const preventBodyScrollAttr = this.getAttribute('prevent-body-scroll');
            if (preventBodyScrollAttr === null) {
                this.shouldPreventBodyScroll = true;
            } else {
                this.shouldPreventBodyScroll = preventBodyScrollAttr === 'true' || preventBodyScrollAttr === '1';
            }

            // Read the prevent-close attribute (data-prevent-close)
            const preventCloseAttr = this.getAttribute('data-prevent-close');
            this.shouldPreventClose = preventCloseAttr === 'true' || preventCloseAttr === '1';

            // If prevent-close is enabled, wrap hidePopover to detect programmatic calls
            if (this.shouldPreventClose) {
                this.wrapHidePopover();
            }
            
            this.initializeBackdrop();

            // Check if this modal uses hover mode
            this.detectHoverMode();

            this.initializeTargetLinks();
            
            return true;
        }
        
        /**
         * Initializes backdrop element if it exists.
         */
        initializeBackdrop() {
            this.backdrop = this.querySelector('[data-popover-backdrop]');
            if (this.backdrop) {
                this.backdrop.style.display = 'none';

                // Add click handler to close modal when backdrop is clicked
                // Only add if data-prevent-close is NOT enabled
                if (!this.shouldPreventClose) {
                    this.handleBackdropClick = this.handleBackdropClick.bind(this);
                    this.backdrop.addEventListener('click', this.handleBackdropClick);
                }
            }
        }

        /**
         * Handles click on backdrop to close the modal.
         * Only active when data-prevent-close is false.
         * @param {Event} event
         */
        handleBackdropClick(event) {
            // Only handle direct clicks on the backdrop, not bubbled events
            if (event.target === this.backdrop) {
                this.closeDialog();
            }
        }
        
        /**
         * Detects if this modal uses hover mode by checking trigger buttons.
         */
        detectHoverMode() {
            if (!this.dialog || !this.dialog.id) return;
            
            // Clear existing trigger buttons
            this.triggerButtons = [];
            
            // Find all trigger buttons that target this dialog
            const triggers = document.querySelectorAll(`[popovertarget="${this.dialog.id}"], [popovertarget="#${this.dialog.id}"]`);
            
            triggers.forEach(trigger => {
                // Check trigger's own attribute
                let triggerType = trigger.getAttribute('data-visibility-trigger');
                
                // If not found, check parent external-popover component
                if (!triggerType) {
                    const externalPopover = trigger.closest('external-popover');
                    if (externalPopover) {
                        triggerType = externalPopover.getAttribute('data-visibility-trigger');
                    }
                }
                
                // If still not found, check closest parent with the attribute
                if (!triggerType) {
                    const parentWithAttr = trigger.closest('[data-visibility-trigger]');
                    if (parentWithAttr) {
                        triggerType = parentWithAttr.getAttribute('data-visibility-trigger');
                    }
                }
                
                if (triggerType === 'hover') {
                    this.isHoverMode = true;
                    // Only add if not already in the array
                    if (!this.triggerButtons.includes(trigger)) {
                        this.triggerButtons.push(trigger);
                    }
                }
            });
            
            // Also check if dialog-popover component has hover mode attribute
            const hoverModeAttr = this.getAttribute('data-hover-mode');
            if (hoverModeAttr === 'true' || hoverModeAttr === '1') {
                this.isHoverMode = true;
            }
        }

        /**
         * Wraps the dialog's hidePopover method to mark programmatic calls.
         * This allows programmatic closes (like from offscreenPopoverObserver) to work
         * even when prevent-close is enabled.
         */
        wrapHidePopover() {
            if (!this.dialog || this.wrappedHidePopover) return;
            
            const self = this;
            const originalHidePopover = this.dialog.hidePopover.bind(this.dialog);
            
            // Store original method reference
            if (!this.dialog._originalHidePopover) {
                this.dialog._originalHidePopover = originalHidePopover;
            }
            
            this.wrappedHidePopover = function() {
                // Use a data attribute to mark programmatic closes
                // This is more reliable than timing-based detection
                // Only set if not already set (to avoid overwriting explicit programmatic closes)
                if (!self.dialog.hasAttribute('data-programmatic-close')) {
                    self.dialog.setAttribute('data-programmatic-close', 'true');
                }
                
                try {
                    const result = originalHidePopover();
                    return result;
                } finally {
                    // Clear the flag after the event handlers have run
                    // Use setTimeout with a small delay to ensure it clears AFTER both beforetoggle and toggle events
                    // This is critical - we need the attribute to persist through both events
                    setTimeout(() => {
                        if (self.dialog.hasAttribute('data-programmatic-close')) {
                            self.dialog.removeAttribute('data-programmatic-close');
                        }
                    }, 50); // Small delay to ensure events have processed
                }
            };
            
            this.dialog.hidePopover = this.wrappedHidePopover;
        }

        initializeTargetLinks() {
            // links whos href start with #
            const targetLinks = this.querySelectorAll('a[href^="#"]');
            targetLinks.forEach(link => {
                link.addEventListener('click', this.handleTargetLinkClick.bind(this));
            });
        }

        handleTargetLinkClick(event) {
            event.preventDefault();
            this.closeDialog();
            const targetId = event.currentTarget.getAttribute('href');
            const targetElement = document.querySelector(targetId);
            if (targetElement) {
                targetElement.scrollIntoView({ behavior: 'smooth' });
            } else {
                console.error('DialogPopover Error: Could not find the target element.', { component: this, targetId });
            }
        }

        /**
         * Adds all necessary event listeners.
         */
        addEventListeners() {
            this.handleToggle = this.handleToggle.bind(this);
            this.handleBeforeToggle = this.handleBeforeToggle.bind(this);
            
            this.dialog.addEventListener('toggle', this.handleToggle);
            this.dialog.addEventListener('beforetoggle', this.handleBeforeToggle);
            
            // Listen for custom close events
            this.handleCloseEvent = this.handleCloseEvent.bind(this);
            document.addEventListener('dialog-popover:close', this.handleCloseEvent);
            
            // Set up hover mode handlers if enabled
            if (this.isHoverMode) {
                this.setupHoverMode();
            }
            
            // Track user interactions to distinguish from programmatic calls
            if (this.shouldPreventClose) {
                this.trackUserInteractions();
            }
            
            // Set up global click handler to detect when another popover is about to open
            this.setupPopoverTriggerDetection();
        }
        
        /**
         * Sets up hover mode event handlers for trigger and dialog.
         */
        setupHoverMode() {
            if (!this.dialog) return;
            
            const HOVER_CLOSE_DELAY = 333; // 333ms delay as specified
            
            // Handle mouse leave on trigger buttons
            this.triggerButtons.forEach(trigger => {
                const handleTriggerMouseLeave = (e) => {
                    // Check if mouse is moving to the dialog
                    const relatedTarget = e.relatedTarget;
                    if (relatedTarget && (this.dialog.contains(relatedTarget) || this.dialog === relatedTarget)) {
                        // Mouse is moving to dialog - check if it's a close button
                        const isMovingToCloseButton = relatedTarget.closest && relatedTarget.closest('popover-close');
                        if (!isMovingToCloseButton) {
                            // Moving to dialog (not close button) - don't close, mouseenter will clear any pending close
                            this.clearHoverCloseTimeout();
                            return;
                        }
                        // Moving to close button - schedule close (close button will handle immediate close if hover)
                    }
                    // Mouse is leaving trigger - schedule close after delay
                    // mouseenter on dialog/trigger will clear this if mouse moves back
                    this.scheduleHoverClose();
                };
                
                trigger.addEventListener('mouseleave', handleTriggerMouseLeave);
                trigger._hoverMouseLeaveHandler = handleTriggerMouseLeave;
                
                // Also clear timeout when mouse enters trigger (in case it re-enters quickly)
                const handleTriggerMouseEnter = () => {
                    this.clearHoverCloseTimeout();
                };
                trigger.addEventListener('mouseenter', handleTriggerMouseEnter);
                trigger._hoverMouseEnterHandler = handleTriggerMouseEnter;
            });
            
            // Handle mouse enter/leave on dialog
            const handleDialogMouseEnter = () => {
                // Clear any pending close when mouse enters dialog
                this.clearHoverCloseTimeout();
            };
            
            const handleDialogMouseLeave = (e) => {
                // Check if mouse is moving to a trigger button
                const relatedTarget = e.relatedTarget;
                if (relatedTarget) {
                    const isMovingToTrigger = this.triggerButtons.some(trigger => 
                        trigger.contains(relatedTarget) || trigger === relatedTarget
                    );
                    
                    if (isMovingToTrigger) {
                        // Mouse is moving to trigger, don't close yet
                        this.clearHoverCloseTimeout();
                        return;
                    }
                }
                
                // Mouse is leaving dialog
                // Schedule close after delay
                // The scheduleHoverClose() function will check if mouse is actually over
                // trigger, dialog, or close button before closing
                this.scheduleHoverClose();
            };
            
            this.dialog.addEventListener('mouseenter', handleDialogMouseEnter);
            this.dialog.addEventListener('mouseleave', handleDialogMouseLeave);
            this.dialog._hoverMouseEnterHandler = handleDialogMouseEnter;
            this.dialog._hoverMouseLeaveHandler = handleDialogMouseLeave;
        }
        
        /**
         * Schedules the dialog to close after the hover delay.
         */
        scheduleHoverClose() {
            this.clearHoverCloseTimeout();
            
            this.hoverCloseTimeout = setTimeout(() => {
                // Check if dialog is still open
                if (!this.dialog || !this.dialog.matches(':popover-open')) {
                    this.hoverCloseTimeout = null;
                    return;
                }
                
                // Use more reliable method to check hover state
                // Check if any trigger button is being hovered
                const isOverTrigger = this.triggerButtons.some(trigger => {
                    // Check if element or any descendant is being hovered
                    return trigger.matches(':hover') || 
                           (document.activeElement && trigger.contains(document.activeElement));
                });
                
                // Check if dialog is being hovered
                // Use a more reliable method: check if dialog or any child has :hover
                let isOverDialog = this.dialog.matches(':hover') || 
                                   this.dialog.contains(document.activeElement);
                
                // Also check if any element inside dialog is currently being hovered
                try {
                    const hoveredElement = document.querySelector(':hover');
                    if (hoveredElement && this.dialog.contains(hoveredElement)) {
                        isOverDialog = true;
                    }
                } catch (e) {
                    // :hover selector might not be supported in some contexts
                }
                
                // If mouse is over trigger or dialog, don't close
                if (isOverTrigger || isOverDialog) {
                    this.hoverCloseTimeout = null;
                    return;
                }
                
                // Check if hovering over any close button (hover or click triggered)
                // If hovering over a hover-triggered close button, it will close immediately
                // If hovering over a click-triggered close button, we still want to close after delay
                const allCloseButtons = this.dialog.querySelectorAll('popover-close');
                const isOverCloseButton = Array.from(allCloseButtons).some(closeBtn => {
                    return closeBtn.matches(':hover') || 
                           (document.activeElement && closeBtn.contains(document.activeElement));
                });
                
                // Only close if mouse is not over trigger, dialog, or any close button
                if (!isOverTrigger && !isOverDialog && !isOverCloseButton) {
                    // Close the dialog
                    // For hover mode, we want to bypass prevent-close (force: true)
                    // since hover-close is the expected behavior for hover modals
                    this.closeDialog(true);
                }
                
                this.hoverCloseTimeout = null;
            }, 333); // 333ms delay
        }
        
        /**
         * Clears any pending hover close timeout.
         */
        clearHoverCloseTimeout() {
            if (this.hoverCloseTimeout) {
                clearTimeout(this.hoverCloseTimeout);
                this.hoverCloseTimeout = null;
            }
        }
        
        /**
         * Handles custom close events for dialog popovers.
         * @param {CustomEvent} event - Custom event with detail: { modalId?: string, closeAll?: boolean, force?: boolean }
         */
        handleCloseEvent(event) {
            const { modalId, closeAll = false, force = false } = event.detail || {};
            
            // If closeAll is true, close this modal (unless it's the one being excluded)
            if (closeAll) {
                // If modalId is specified and this is that modal, skip it
                if (modalId && this.dialog.id === modalId) {
                    return;
                }
                // Close this modal with force flag if provided
                this.closeDialog(force);
                return;
            }
            
            // If modalId is specified and this matches, close it
            if (modalId && this.dialog.id === modalId) {
                this.closeDialog(force);
            }
        }

        /**
         * Tracks user interactions (clicks, key presses) to distinguish
         * user-initiated closes from programmatic closes.
         * Note: This is kept for potential future use, but the primary detection
         * method is now the data-programmatic-close attribute set by wrapHidePopover.
         */
        trackUserInteractions() {
            // This method is kept for potential future enhancements
            // Currently, programmatic closes are detected via the data-programmatic-close attribute
        }
        
        /**
         * Sets up detection for clicks on elements that open popovers.
         * This allows us to close the current modal even if prevent-close is enabled
         * when the user clicks to open a new modal.
         */
        setupPopoverTriggerDetection() {
            // Use a single global handler for efficiency
            // NOTE: This is kept for potential future use, but we no longer close
            // modals with prevent-close when opening another modal
            if (!DialogPopover.globalClickHandlerAdded) {
                document.addEventListener('click', (e) => {
                    // Check if the clicked element or its ancestor has a popovertarget attribute
                    const trigger = e.target.closest('[popovertarget]');
                    if (trigger && (trigger.tagName === 'BUTTON' || trigger.tagName === 'A' || trigger.hasAttribute('popovertarget'))) {
                        const targetId = trigger.getAttribute('popovertarget');
                        if (targetId) {
                            const targetPopover = document.getElementById(targetId);
                            if (targetPopover) {
                                // Mark which modal is about to open (before beforetoggle fires)
                                DialogPopover.openingModalId = targetId;
                                
                                // Clear the opening modal ID after a delay (in case the modal doesn't actually open)
                                setTimeout(() => {
                                    if (DialogPopover.openingModalId === targetId && !targetPopover.matches(':popover-open')) {
                                        DialogPopover.openingModalId = null;
                                    }
                                }, 100);
                            }
                        }
                    }
                }, true); // Use capture phase to catch early
                DialogPopover.globalClickHandlerAdded = true;
            }
        }
        
        /**
         * Cleans up all event listeners and restores original methods.
         */
        removeEventListeners() {
            if (this.dialog) {
                this.dialog.removeEventListener('toggle', this.handleToggle);
                this.dialog.removeEventListener('beforetoggle', this.handleBeforeToggle);
                
                // Remove hover mode handlers
                if (this.dialog._hoverMouseEnterHandler) {
                    this.dialog.removeEventListener('mouseenter', this.dialog._hoverMouseEnterHandler);
                }
                if (this.dialog._hoverMouseLeaveHandler) {
                    this.dialog.removeEventListener('mouseleave', this.dialog._hoverMouseLeaveHandler);
                }
            }
            
            // Remove hover handlers from trigger buttons
            this.triggerButtons.forEach(trigger => {
                if (trigger._hoverMouseLeaveHandler) {
                    trigger.removeEventListener('mouseleave', trigger._hoverMouseLeaveHandler);
                    delete trigger._hoverMouseLeaveHandler;
                }
                if (trigger._hoverMouseEnterHandler) {
                    trigger.removeEventListener('mouseenter', trigger._hoverMouseEnterHandler);
                    delete trigger._hoverMouseEnterHandler;
                }
            });
            
            // Clear any pending hover close timeout
            this.clearHoverCloseTimeout();
            
            // Remove custom close event listener
            if (this.handleCloseEvent) {
                document.removeEventListener('dialog-popover:close', this.handleCloseEvent);
            }

            // Remove backdrop click listener
            if (this.backdrop && this.handleBackdropClick) {
                this.backdrop.removeEventListener('click', this.handleBackdropClick);
            }
            
            // Restore original hidePopover if it was wrapped
            if (this.wrappedHidePopover && this.dialog) {
                // Note: We can't fully restore the original, but we can remove our wrapper
                // by checking if hidePopover still matches our wrapped version
                if (this.dialog.hidePopover === this.wrappedHidePopover) {
                    // Find the original by looking at the prototype or storing it
                    // For now, we'll just leave it wrapped since the component is being removed
                }
            }
        }

        // --------------------------------------------------------------------
        // EVENT HANDLERS
        // --------------------------------------------------------------------

        /**
         * Handles the beforetoggle event to prepare for popover state changes.
         * @param {Event} event
         */
        handleBeforeToggle(event) {
            const { newState } = event;
            
            if (newState === 'open') {
                // Mark this modal as the one opening
                DialogPopover.openingModalId = this.dialog.id;
                
                // Only close other modals if they don't have prevent-close enabled
                // Modals with prevent-close should remain open when opening another modal
                this.handleOpeningNewModal();
                this.handleDialogOpen();
            } else if (newState === 'closed') {
                // Hide backdrop if it exists
                if (this.backdrop) {
                    this.backdrop.style.display = 'none';
                }
                
                // Check if this is a programmatic close (marked by wrapped hidePopover or data attribute)
                const isProgrammatic = this.dialog.hasAttribute('data-programmatic-close') || 
                                       this.isProgrammaticClose;
                
                // CRITICAL: Check if this is an offscreen observer close
                // When trigger element comes back into view, we MUST allow the close
                // regardless of prevent-close setting
                const isOffscreenObserverClose = this.dialog.hasAttribute('data-offscreen-observer-close');
                
                // If prevent-close is enabled, prevent the dialog from closing
                // UNLESS this is an offscreen observer close (trigger came back into view)
                // This handles clicks outside, ESC key, and other automatic close triggers
                // But allow programmatic closes (like from offscreenPopoverObserver)
                // NOTE: We do NOT close modals with prevent-close when opening another modal
                if (this.shouldPreventClose && !isProgrammatic && !isOffscreenObserverClose) {
                    // Try to prevent the toggle from happening
                    // Note: beforetoggle is cancelable according to the spec
                    if (event.cancelable) {
                        event.preventDefault();
                    }
                    // If preventDefault() doesn't work or event is not cancelable,
                    // the toggle event handler will catch it and reopen the dialog
                    return;
                }
                
                // Clear the opening modal ID if this modal is closing
                if (DialogPopover.openingModalId === this.dialog.id) {
                    DialogPopover.openingModalId = null;
                }
                
                // Only restore scroll if the dialog is actually closing
                this.restoreBodyScroll();
            }
        }
        
        /**
         * Handles opening a new modal when another modal is open.
         * Only closes modals that don't have prevent-close enabled.
         * Modals with prevent-close should remain open.
         * IMPORTANT: If the new modal is nested inside an existing modal, we should NOT close the parent.
         */
        handleOpeningNewModal() {
            // Find all open dialogs except this one
            // Note: This is called in beforetoggle, so this.dialog might not be :popover-open yet
            // We check all open dialogs and close any that aren't this one AND don't have prevent-close
            // Query both native popovers and fallback popover-elements
            const openDialogs = document.querySelectorAll('[popover]:popover-open, popover-element.popover-open');
            
            openDialogs.forEach(dialog => {
                if (dialog !== this.dialog) {
                    // Check if the new modal being opened is nested inside this existing modal
                    // If it is, we should NOT close the parent modal
                    
                    // Method 1: Check if the new modal's dialog-popover component is inside the existing modal's dialog
                    const newModalComponent = this.dialog.closest('dialog-popover');
                    const existingModalComponent = dialog.closest('dialog-popover');
                    const isNestedComponent = existingModalComponent && 
                                             newModalComponent && 
                                             existingModalComponent.contains(newModalComponent);
                    
                    // Method 2: Check if the new modal's dialog element is inside the existing modal's dialog
                    const isNestedDialog = dialog.contains(this.dialog);
                    
                    // Method 3: Check if the trigger button for the new modal is inside the existing modal's dialog
                    // Find the button that opens this new modal by checking for popovertarget attribute
                    const triggerButton = document.querySelector(`[popovertarget="${this.dialog.id}"]`);
                    const isTriggerInsideExisting = triggerButton && dialog.contains(triggerButton);
                    
                    const isNestedChild = isNestedComponent || isNestedDialog || isTriggerInsideExisting;
                    
                    // If the new modal is nested inside the existing modal, don't close the parent
                    if (isNestedChild) {
                        return; // Skip closing this dialog
                    }
                    
                    // Find the dialog-popover component for this dialog
                    const dialogComponent = dialog.closest('dialog-popover');
                    
                    // Only close if prevent-close is NOT enabled
                    // Modals with prevent-close should remain open when opening another modal
                    if (dialogComponent && !dialogComponent.shouldPreventClose) {
                        // Close the other modal programmatically
                        dialogComponent.closeDialog();
                    } else if (!dialogComponent) {
                        // If no component found, check if it has prevent-close attribute
                        const hasPreventClose = dialog.closest('[data-prevent-close="true"]') !== null;
                        if (!hasPreventClose) {
                            // Only close if prevent-close is not enabled
                            dialog.setAttribute('data-programmatic-close', 'true');
                            if (typeof dialog.hidePopover === 'function') {
                                dialog.hidePopover();
                            }
                            Promise.resolve().then(() => {
                                dialog.removeAttribute('data-programmatic-close');
                            });
                        }
                    }
                    // If prevent-close is enabled, do nothing - let it stay open
                }
            });
        }

        /**
         * Handles the toggle event after popover state has changed.
         * @param {Event} event
         */
        handleToggle(event) {
            const { newState } = event;
            
            if (newState === 'open') {
                // Fallback for browsers that don't support beforetoggle
                if (!this.isScrollLocked) {
                    this.handleDialogOpen();
                }
                
                // Clear the opening modal ID once the modal is actually open
                if (DialogPopover.openingModalId === this.dialog.id) {
                    DialogPopover.openingModalId = null;
                }
                
                // Clear instance flag
                this.isOpeningAnotherModal = false;
            } else if (newState === 'closed') {
                // Check if this is a programmatic close (marked by wrapped hidePopover or data attribute)
                const hasProgrammaticAttr = this.dialog.hasAttribute('data-programmatic-close');
                const isProgrammaticFlag = this.isProgrammaticClose;
                const isProgrammatic = hasProgrammaticAttr || isProgrammaticFlag;
                
                // CRITICAL: Check if this is an offscreen observer close
                // When trigger element comes back into view, we MUST allow the close
                // and NOT reopen the dialog
                const isOffscreenObserverClose = this.dialog.hasAttribute('data-offscreen-observer-close');
                
                // CRITICAL: Check if the trigger element is visible
                // This is the PRIMARY check - if trigger is visible, dialog should stay closed
                // This works even if the offscreen observer flag was cleared prematurely
                const triggerIsVisible = this.isTriggerElementVisible();
                
                // Clear the offscreen observer flag now that we've checked it
                // This must happen AFTER we check it but before we decide whether to reopen
                if (isOffscreenObserverClose) {
                    // Clear the flag now that we've used it
                    this.dialog.removeAttribute('data-offscreen-observer-close');
                }
                
                // CRITICAL: If trigger element is visible, NEVER reopen the dialog
                // This is the primary safeguard - trigger visibility takes precedence over prevent-close
                if (triggerIsVisible) {
                    // Clear flags and return - dialog should stay closed
                    this.isOpeningAnotherModal = false;
                    if (this.isScrollLocked) {
                        this.restoreBodyScroll();
                    }
                    return;
                }
                
                // Fallback: If prevent-close is enabled and dialog closed unexpectedly,
                // reopen it (this handles cases where preventDefault() didn't work)
                // BUT don't reopen if this was a programmatic close OR an offscreen observer close
                // NOTE: We already checked trigger visibility above, so we know it's not visible here
                // NOTE: We allow modals with prevent-close to remain open even when another modal opens
                // ALSO: Don't reopen if another modal is now open (legitimate reason to stay closed)
                if (this.shouldPreventClose && !isProgrammatic && !isOffscreenObserverClose) {
                    // Check if another modal is open - if so, don't reopen this one
                    // Query both native popovers and fallback popover-elements
                    const otherOpenModal = document.querySelector('[popover]:popover-open, popover-element.popover-open');
                    if (otherOpenModal && otherOpenModal !== this.dialog) {
                        // Another modal is open, so this close is legitimate
                        return;
                    }

                    // Reopen the dialog immediately if it closed when it shouldn't have
                    requestAnimationFrame(() => {
                        if (this.dialog && !this.dialog.matches(':popover-open')) {
                            // Double-check no other modal opened in the meantime
                            const checkOtherModal = document.querySelector('[popover]:popover-open, popover-element.popover-open');
                            if (!checkOtherModal) {
                                // Also double-check trigger is still not visible before reopening
                                const triggerStillNotVisible = !this.isTriggerElementVisible();
                                if (triggerStillNotVisible) {
                                    this.dialog.showPopover();
                                }
                            }
                        }
                    });
                    return;
                }
                // Clear flags
                this.isOpeningAnotherModal = false;
                
                // Only restore scroll if the dialog is actually closing
                if (this.isScrollLocked) {
                    this.restoreBodyScroll();
                }
            }
        }

        // --------------------------------------------------------------------
        // DIALOG OPEN HANDLING
        // --------------------------------------------------------------------

        /**
         * Handles all actions needed when dialog opens.
         */
        handleDialogOpen() {
            // Re-check for hover mode when dialog opens (triggers might be added dynamically)
            if (!this.isHoverMode) {
                this.detectHoverMode();
                if (this.isHoverMode && !this.dialog._hoverMouseEnterHandler) {
                    this.setupHoverMode();
                }
            }
            
            // Show backdrop if it exists
            if (this.backdrop) {
                this.backdrop.style.display = 'block';
            }
            
            if (this.shouldScrollToTop) {
                this.scrollToTop();
            }
            if (this.shouldPreventBodyScroll) {
                this.preventBodyScroll();
            }
        }

        /**
         * Scrolls the page to the top smoothly.
         */
        scrollToTop() {
            try {
                window.scrollTo({
                    top: 0,
                    left: 0,
                    behavior: 'smooth'
                });
                
                // For browsers that don't support smooth scrolling
                if (window.pageYOffset !== 0) {
                    window.scrollTo(0, 0);
                }
                
            } catch (error) {
                console.error('DialogPopover Error: Failed to scroll to top.', { component: this, error });
                // Fallback to instant scroll
                try {
                    window.scrollTo(0, 0);
                } catch (fallbackError) {
                    console.error('DialogPopover Error: Failed to scroll to top (fallback).', { component: this, error: fallbackError });
                }
            }
        }

        // --------------------------------------------------------------------
        // SCROLL MANAGEMENT
        // --------------------------------------------------------------------

        /**
         * Prevents body scroll by storing current position and applying scroll lock.
         */
        preventBodyScroll() {
            if (this.isScrollLocked) return;
            
            try {
                // Store the current scroll position (after potential scroll-to-top)
                this.originalScrollPosition = window.pageYOffset || document.documentElement.scrollTop;
                
                // Apply scroll lock styles that preserve sticky positioning
                document.body.style.position = 'fixed';
                document.body.style.top = `-${this.originalScrollPosition}px`;
                document.body.style.width = '100%';
                
                // Add class for additional styling if needed
                document.body.classList.add(DialogPopover.CONSTANTS.SCROLL_LOCK_CLASS);
                
                // Store scroll position as data attribute for debugging
                document.body.setAttribute(DialogPopover.CONSTANTS.SCROLL_POSITION_ATTRIBUTE, this.originalScrollPosition);
                
                this.isScrollLocked = true;
                
            } catch (error) {
                console.error('DialogPopover Error: Failed to prevent body scroll.', { component: this, error });
            }
        }

        /**
         * Restores body scroll to the original position.
         */
        restoreBodyScroll() {
            if (!this.isScrollLocked) return;

            try {
                // Temporarily disable smooth scrolling for instant position restore
                document.documentElement.style.scrollBehavior = 'auto';

                // Remove scroll lock styles from both html and body
                document.body.style.position = '';
                document.body.style.top = '';
                document.body.style.width = '';
                document.documentElement.style.overflow = '';
                document.body.style.overflow = '';

                // Remove class and data attribute
                document.body.classList.remove(DialogPopover.CONSTANTS.SCROLL_LOCK_CLASS);
                document.body.removeAttribute(DialogPopover.CONSTANTS.SCROLL_POSITION_ATTRIBUTE);

                // Restore the original scroll position instantly
                // Note: If scroll-to-top was used, originalScrollPosition will be 0
                window.scrollTo(0, this.originalScrollPosition);

                this.isScrollLocked = false;

                // Remove the inline scroll-behavior style after rendering, letting CSS take over
                setTimeout(() => {
                    document.documentElement.style.scrollBehavior = '';
                }, 50);

            } catch (error) {
                console.error('DialogPopover Error: Failed to restore body scroll.', { component: this, error });
            }
        }

        // --------------------------------------------------------------------
        // UTILITY METHODS
        // --------------------------------------------------------------------

        /**
         * Manually opens the dialog popover.
         */
        openDialog() {
            if (this.dialog && !this.dialog.matches(':popover-open')) {
                this.dialog.showPopover();
            }
        }

        /**
         * Checks if any trigger element for this dialog is currently visible in the viewport.
         * This is used to determine if the dialog should stay closed when prevent-close is enabled.
         * @returns {boolean} True if any trigger element is visible
         */
        isTriggerElementVisible() {
            if (!this.dialog || !this.dialog.id) return false;
            
            // Find all elements with data-popover-when-offscreen that point to this dialog
            const triggerElements = document.querySelectorAll(`[data-popover-when-offscreen="${this.dialog.id}"], [data-popover-when-offscreen="#${this.dialog.id}"]`);
            
            if (triggerElements.length === 0) {
                return false;
            }
            
            // Check if any trigger element is currently intersecting the viewport
            for (const trigger of triggerElements) {
                const rect = trigger.getBoundingClientRect();
                
                // Check if element is partially visible (more lenient check)
                const isPartiallyVisible = rect.top < (window.innerHeight || document.documentElement.clientHeight) &&
                                          rect.bottom > 0 &&
                                          rect.left < (window.innerWidth || document.documentElement.clientWidth) &&
                                          rect.right > 0;
                
                if (isPartiallyVisible) {
                    return true;
                }
            }
            
            return false;
        }

        closeDialog(forceClose = false) {
            if (this.dialog && this.dialog.matches(':popover-open')) {
                // Mark this as a programmatic close so it won't be prevented
                // Set both flags BEFORE calling hidePopover to ensure beforetoggle sees them
                this.isProgrammaticClose = true;
                this.dialog.setAttribute('data-programmatic-close', 'true');
                
                // If this is a force close, temporarily disable prevent-close
                // Store original value to restore if needed
                const originalPreventClose = this.shouldPreventClose;
                if (forceClose) {
                    this.shouldPreventClose = false;
                    this.dialog.setAttribute('data-offscreen-observer-close', 'true');
                } else if (this.dialog.hasAttribute('data-offscreen-observer-close')) {
                    this.dialog.setAttribute('data-offscreen-observer-close', 'true');
                }
                
                // Use the wrapped hidePopover if available, otherwise use the original
                // The wrapped version also sets the attribute, but we've already set it here
                // to ensure it's available when beforetoggle fires
                if (this.wrappedHidePopover) {
                    this.wrappedHidePopover();
                } else {
                    this.dialog.hidePopover();
                }
                
                // Restore prevent-close setting if we temporarily disabled it
                if (forceClose) {
                    this.shouldPreventClose = originalPreventClose;
                }
                
                // Reset the programmatic flag after the close completes
                // BUT DON'T clear data-offscreen-observer-close here - let handleToggle clear it
                // so it's available when handleToggle checks it
                Promise.resolve().then(() => {
                    this.isProgrammaticClose = false;
                    if (this.dialog.hasAttribute('data-programmatic-close')) {
                        this.dialog.removeAttribute('data-programmatic-close');
                    }
                    // Note: data-offscreen-observer-close will be cleared in handleToggle after checking
                });
            }
        }

        /**
         * Toggles the dialog popover state.
         */
        toggleDialog() {
            if (this.dialog) {
                if (this.dialog.matches(':popover-open')) {
                    this.closeDialog();
                } else {
                    this.openDialog();
                }
            }
        }
    }

    customElements.define('dialog-popover', DialogPopover);
    
    // Export class to global scope for use by external-popover
    window.DialogPopover = DialogPopover;
    
    // Note: closeDialogPopover is now defined in utils.js as a shared utility
}
